import lodash from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { ReactElement } from 'react'
import { useForm } from 'react-final-form'
import styled from 'styled-components'

import Button from '../../../../../../1stlog-ui/src/components/Button'
import ButtonGroup from '../../../../../../1stlog-ui/src/components/ButtonGroup'
import {
  useField,
  useFieldContext,
} from '../../../../../../1stlog-ui/src/components/Field'
import LoaderOverlay from '../../../../../../1stlog-ui/src/components/LoaderOverlay'
import { usePrevious } from '../../../../../../1stlog-ui/src/hooks'
import { request, toast } from '../../../../../../1stlog-ui/src/utils'
import { TOrderEntryCustomsItem } from '../../../types'
import { serializeValues } from '../CustomsList/CustomsItemEditor'
import CustomsItemForm from './CustomsItemForm'

const Wrapper = styled.div`
  position: relative;
`

type TCustomsItemProps = {
  allowDatabase?: boolean
  index: number
  exportCountryField: string
  importCountryField: string
  newElement: TOrderEntryCustomsItem
  isOptional?: boolean
  parentName: string
}

const CustomsItem = (props: TCustomsItemProps): ReactElement => {
  const {
    allowDatabase = true,
    index,
    exportCountryField,
    importCountryField,
    newElement,
    isOptional,
    parentName,
  } = props

  const [isLoading, setIsLoading] = useState(false)

  const exportCountry = useField(exportCountryField, { ignorePrefix: true })
  const importCountry = useField(importCountryField, { ignorePrefix: true })

  const [id, setId] = useField('id', { asState: true })

  const articleNumber = useField('articleNumber')

  const { prefix: name = '' } = useFieldContext()

  const form = useForm()

  const value = useField('value')

  const currency = useMemo(() => value?.currency, [value])

  const previousCurrency = usePrevious(currency)
  useEffect(() => {
    if (previousCurrency && previousCurrency !== currency) {
      form?.batch(() => {
        lodash
          .get(form?.getState()?.values, parentName)
          ?.forEach((item, index) => {
            if (item?.value?.currency !== currency) {
              form.change(`${parentName}.${index}.value.currency`, currency)
            }
          })
      })
      setTimeout(() => {
        form.change('valueOfGoods.currency', currency)
      })
    }
  }, [currency])

  // Check if value of goods currency was manually changed
  const [valueOfGoods] = useField('valueOfGoods', {
    asState: true,
    ignorePrefix: true,
  })
  const valueOfGoodsCurrency = useMemo(
    () => valueOfGoods?.currency,
    [valueOfGoods],
  )
  const previousValueOfGoodsCurrency = usePrevious(valueOfGoodsCurrency)
  useEffect(() => {
    if (
      previousValueOfGoodsCurrency &&
      valueOfGoodsCurrency &&
      previousValueOfGoodsCurrency !== valueOfGoodsCurrency
    ) {
      form?.batch(() => {
        lodash
          .get(form?.getState()?.values, parentName)
          ?.forEach((item, index) => {
            if (item?.value?.currency !== valueOfGoodsCurrency) {
              form.change(
                `${parentName}.${index}.value.currency`,
                valueOfGoodsCurrency,
              )
            }
          })
      })
    }
  }, [valueOfGoodsCurrency])

  const [customsItem, setCustomsItem] = useField(name, {
    asState: true,
    ignorePrefix: true,
  })

  const erase = (): void => {
    setCustomsItem(newElement)
  }

  const serializeRemoteValues = (): any => {
    return serializeValues({
      ...customsItem,
      tariffNumber: lodash.get(customsItem, 'exporttariffNumber'),
    })
  }

  const onApplyValues = (values): void => {
    const {
      value,
      valueCurrency,
      tariffNumber,
      exporttariffNumber,
      importtariffNumber,
    } = values
    const newItem = {
      ...lodash.pick(customsItem, ['count']),
      ...lodash.pick(values, [
        'id',
        'articleNumber',
        'description',
        'originCountry',
        'netWeight',
        'grossWeight',
      ]),
      value: { value, currency: valueCurrency },
      exporttariffNumber: exporttariffNumber || tariffNumber,
      importtariffNumber,
    }
    setCustomsItem(newItem)
  }

  const save = async (): Promise<void> => {
    // TODO: validate fields
    const payload = serializeRemoteValues()
    try {
      setIsLoading(true)
      const { data } = await request({
        method: 'put',
        url: '/api/customs',
        data: payload,
      })
      toast.success(i18n.t('toast_data_saved', 'Eintrag wurde gespeichert.'))
      setId(lodash.get(data, 'id'))
    } catch (err) {
      console.error('CustomsItem/save error', err)
      toast.error(
        i18n.t(
          'toast_error_data_saved',
          'Eintrag konnte nicht gespeichert werden!',
        ),
      )
    } finally {
      setIsLoading(false)
    }
  }

  const update = async (): Promise<void> => {
    // TODO: validate fields
    const payload = serializeRemoteValues()
    try {
      setIsLoading(true)
      const { data } = await request({
        method: 'patch',
        url: `/api/customs/${lodash.get(payload, 'id')}`,
        data: payload,
      })
      toast.success(i18n.t('toast_data_updated', 'Eintrag wurde aktualisiert.'))
    } catch (err) {
      console.error('CustomsItem/save error', err)
      toast.error(
        i18n.t(
          'toast_error_data_updated',
          'Eintrag konnte nicht aktualisiert werden!',
        ),
      )
    } finally {
      setIsLoading(false)
    }
  }

  const removeRemote = async (): Promise<void> => {
    const payload = serializeRemoteValues()
    try {
      setIsLoading(true)
      const { data } = await request({
        method: 'delete',
        url: `/api/customs/${lodash.get(payload, 'id')}`,
        data: payload,
      })
      toast.success(i18n.t('toast_data_deleted', 'Eintrag wurde gelöscht.'))
      // erase()
      // TODO: just clean ID
      setId(undefined)
    } catch (err) {
      console.error('CustomsItem/save error', err)
      toast.error(
        i18n.t(
          'toast_error_data_deleted',
          'Eintrag konnte nicht gelöscht werden!',
        ),
      )
    } finally {
      setIsLoading(false)
    }
  }

  const canSave =
    articleNumber && articleNumber !== '' && articleNumber.length >= 1

  return (
    <Wrapper className="ml-2">
      <LoaderOverlay isLoading={isLoading}>
        {i18n.t('is_loading', 'Wird geladen...')}
      </LoaderOverlay>
      <div className="flex-fill">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h4>#{index + 1}. </h4>
            {/* <ButtonGroup size="xs">
              <Button light icon="fas fa-pencil-alt">
                Bearbeiten
              </Button>
            </ButtonGroup> */}
          </div>
          {allowDatabase && (
            <ButtonGroup size="xs">
              <Button
                onClick={erase}
                light
                icon="fas fa-eraser"
                tooltip={i18n.t(
                  'reset_form_fields_for_entry',
                  'Formularfelder dieses Eintrages zurücksetzen',
                )}
              ></Button>
              <Button
                onClick={save}
                info
                icon="fas fa-save"
                tooltip={i18n.t(
                  'save_in_customs_database',
                  'In Zolldatenbank speichern',
                )}
                disabled={!canSave}
              >
                {i18n.t('save_as_new_entry', 'Als neuen Eintrag Speichern')}
              </Button>
              {true && (
                <Button
                  disabled={!id || !canSave}
                  onClick={update}
                  info
                  icon="fas fa-edit"
                  tooltip={i18n.t(
                    'update_entry_tooltip',
                    'Zugrundeliegenden Eintrag in der Zolldatenbank mit aktuellen Werten überschreiben',
                  )}
                >
                  {i18n.t('update_entry', 'Eintrag aktualisieren')}
                </Button>
              )}

              <Button
                disabled={!id}
                onClick={removeRemote}
                danger
                icon="fas fa-trash-alt"
                tooltip={i18n.t(
                  'remove_entry_tooltip',
                  'Eintrag aus der Zolldatenbank entfernen',
                )}
              >
                {i18n.t('remove_entry', 'Löschen')}
              </Button>
            </ButtonGroup>
          )}
        </div>
        <CustomsItemForm
          key={isOptional}
          exportCountry={exportCountry}
          importCountry={importCountry}
          onApplyValues={onApplyValues}
          isOptional={isOptional}
        />
      </div>
    </Wrapper>
  )
}

export default CustomsItem
